import NextLink from 'next/link';
import { type FC, useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Flex,
  Link,
  Spinner,
  Text,
} from '@eqi-investimentos/react';
import { Grid } from '@chakra-ui/react';
import { CTA_Button } from '../../interface/CTA_Button';
import {
  ContentModeEnum,
  type Content,
  type ContentMode,
} from '@/modules/Content/interface/Content';
import { useT } from '@/libs/i18n/utils/useT';
import { PATHS } from '@/config/paths';
import { salesforceOpportunityConfig } from '@/config/salesForce';
import { OpenAccountDrawer } from '@/components/modules/OpenAccountDrawer';
import { useCreateSFOpportunity } from '@/modules/Customer/http/client/mutations/useCreateSFOpportunity';
import { useSendClickEvent } from '@/modules/Customer/http/client/mutations/useSendClickEvent';
import { ContentRelatedContent } from '@/modules/Research/ui/ContentRelatedContent';
import { Thumb } from '@/components/elements/Thumb';
import { IfRender } from '@/utils/jsx';
import { truncateString } from '@/utils/string';
import { useGetCTAContent } from '../../http/client/useGetCTAContent';
import { ContentActions } from '../ContentActions';
import { CTAButton } from '../CTAButton';
import { ContentRelatedLinks } from './ContentRelatedLinks';

export const ContentFullDetails: FC<ContentFullDetailsProps> = ({
  authors,
  bookmarkPending,
  content,
  contentMode,
  isEqiCustomer,
  isBookmarked,
  isAdvisoryPage,
  programType,
  handleBookmark,
}) => {
  const t = useT();

  const { mutate: sendClickEvent } = useSendClickEvent();
  const { mutate: createSFOpportunity } = useCreateSFOpportunity();

  const { data: dataCTAContent } = useGetCTAContent(content.slug);

  const [expanded, setExpanded] = useState(false);

  const isProgramPage =
    contentMode !== ContentModeEnum.TEXT && programType !== 'recommendation';

  const sendOpportunity = () => {
    createSFOpportunity({
      action: salesforceOpportunityConfig.banking.content.action,
      product: salesforceOpportunityConfig.banking.content.product,
    });

    sendClickEvent({
      label: salesforceOpportunityConfig.sendClickEvent.openAccount.label(
        window.location.pathname
      ),
    });
  };

  const handleSendClick = () => {
    sendClickEvent({
      label: salesforceOpportunityConfig.sendClickEvent.recommnendation.label(
        t('screens.content.downloadReport'),
        window.location.pathname
      ),
    });
  };

  return (
    <IfRender
      condition={
        isProgramPage ||
        (contentMode !== ContentModeEnum.TEXT &&
          programType === 'recommendation')
      }
    >
      <Grid gridTemplateColumns="60% 1fr" gap="6xl" w="full">
        <Flex direction="column" gap="l">
          <Flex direction="column">
            <Flex justifyContent="space-between" alignItems="center" w="full">
              <Text size="h5" fontWeight="medium" color="content.primary">
                {content?.title}
              </Text>
              <IfRender condition={bookmarkPending}>
                <Flex
                  sx={{
                    '.chakra-spinner': {
                      color: 'content.brand !important',
                      w: '1.25rem !important',
                      h: '1.25rem !important',
                    },
                  }}
                >
                  <Spinner />
                </Flex>
              </IfRender>
              <IfRender condition={!bookmarkPending && !isProgramPage}>
                <ContentActions
                  bookmarkContent={handleBookmark}
                  has_audio={content.has_audio}
                  has_video={content.has_video}
                  isBookmarked={isBookmarked}
                  mode="others"
                />
              </IfRender>
            </Flex>
            <IfRender condition={isProgramPage}>
              <Text size="xl" color="content.brand">
                {authors}
              </Text>
            </IfRender>
          </Flex>
          {content?.summary_striped && (
            <Accordion
              allowToggle
              onChange={(index) => setExpanded(index === 1)}
              bg="background.brandLight"
              borderRadius="xs"
              p="l"
            >
              <AccordionItem border="none">
                <AccordionButton
                  _expanded={{ display: 'none' }}
                  style={{ alignItems: 'flex-start' }}
                  p="0"
                >
                  <Text size="xl" textAlign="left">
                    {truncateString(content.summary_striped, 150)}
                  </Text>
                  <AccordionIcon color="content.primary" />
                </AccordionButton>
                <AccordionButton
                  _expanded={{
                    display: 'flex !important',
                    justifyContent: 'flex-end',
                    marginBottom: '-20px',
                  }}
                  style={{ display: 'none' }}
                  p="0"
                >
                  <AccordionIcon color="content.primary" />
                </AccordionButton>
                <AccordionPanel
                  sx={{
                    padding: '0 !important',
                    color: 'content.primary',
                    p: {
                      fontSize: 'xl',
                      lineHeight: 'xl',
                    },
                    'strong,b': {
                      fontWeight: 'semibold',
                    },
                    a: {
                      color: 'content.brand',
                    },
                    'h1,h2,h3,h4,h5,h6': {
                      fontSize: '2xl',
                      fontWeight: 'semibold',
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: content.summary }}
                />
              </AccordionItem>
            </Accordion>
          )}
          <Flex py="l">
            <IfRender condition={!isProgramPage}>
              <Flex gap="s" w="full">
                <NextLink href={`${PATHS.hubs.author}/${content.author_slug}`}>
                  <Thumb
                    src={content.author_thumb}
                    width={48}
                    height={48}
                    radius={100}
                  />
                </NextLink>
                <Flex flexDir="column" justifyContent="center" gap="2xs">
                  <Link
                    as={NextLink}
                    href={`${PATHS.hubs.author}/${content.author_slug}`}
                    fontSize="xl"
                    fontWeight="medium"
                    lineHeight="xl"
                  >
                    {authors}
                  </Link>
                  <Text size="xl" fontWeight="light" color="content.secondary">
                    {content.published_at}
                  </Text>
                </Flex>
              </Flex>
            </IfRender>
            <IfRender
              condition={
                !isEqiCustomer ||
                isAdvisoryPage ||
                !!content.report ||
                !!dataCTAContent?.data
              }
            >
              <Flex gap="m" h="3.25rem">
                {isEqiCustomer && !!dataCTAContent?.data && (
                  <CTAButton data={dataCTAContent.data} />
                )}
                {!isEqiCustomer ? (
                  <OpenAccountDrawer
                    screen="research"
                    handleBeforeDrawerOpen={sendOpportunity}
                  />
                ) : (
                  isAdvisoryPage && (
                    <Button size="md" w="12rem" onClick={sendOpportunity}>
                      {t('screens.myAdvisory.speakWithAdvisor')}
                    </Button>
                  )
                )}
                {!!content.report && (
                  <Button size="md" variant="outline" w="12rem">
                    <Link
                      download
                      href={content.report}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleSendClick}
                      p="0.75rem 3rem"
                      _hover={{ textDecoration: 'none' }}
                    >
                      {t('screens.content.downloadReport')}
                    </Link>
                  </Button>
                )}
              </Flex>
            </IfRender>
          </Flex>

          <ContentRelatedLinks relatedLinks={content.related_links} />
        </Flex>
        <ContentRelatedContent
          expanded={expanded}
          programId={content.program_id}
        />
      </Grid>
    </IfRender>
  );
};

type ContentFullDetailsProps = {
  authors: string;
  bookmarkPending: boolean;
  content: Content;
  contentMode: ContentMode;
  dataCTAContent?: CTA_Button;
  isEqiCustomer: boolean;
  isBookmarked: boolean;
  isAdvisoryPage: boolean;
  programType: string;
  handleBookmark: () => void;
};
